import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import OverviewTab from "./OverviewTab";
import SalaryStructuresTab from "../SalaryStructures";
import BankAccountsTab from "../BankDetails";
import EmployeePayslipsTab from "./EmployeePayslipsTab";
import EmployeeAdvancesTab from "./EmployeeAdvancesTab";
import { DateOnly } from "components/common/Date";
import { useEmployeeOverview } from "./EmployeeOverviewContext";
import Avatar from "components/common/Avatar";
import {
  departmentDisplayName,
  designationDisplayName,
  employmentCategoryDisplayName,
} from "../types/employee.types";
import {
  EmployeeOverviewTabParam,
  employeeOverviewTabIdFromSearchParam,
  isValidEmployeeOverviewTabParam,
  searchParamForEmployeeOverviewTabId,
} from "./overviewTabSearchParams";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import { deleteUser } from "features/Users/services/UserService";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useProfile } from "features/Authentication/hooks/useProfile";

const Section = () => {
  const { can } = useAccess();
  const { user: profileUser } = useProfile();
  const canEditEmployee = can(Permissions.EMPLOYEE.UPDATE);
  const canDeleteUser = can(Permissions.USER.DELETE);
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const canReadSalaryStructures = can(
    Permissions.EMPLOYEE_SALARY_STRUCTURE.READ,
  );
  const canReadEmployeeAdvances = can(Permissions.EMPLOYEE_ADVANCE.READ);
  const { employee } = useEmployeeOverview();
  const isOwnProfile =
    profileUser?.employee?.id != null &&
    profileUser.employee.id === employee?.id;
  const handleDelete = async () => {
    if (!employee?.userId) return;
    try {
      const res = await deleteUser(employee.userId);
      if (res.success) {
        toast.success(res.message ?? "User deleted.");
        navigate("/employees");
      } else {
        toast.error(res.message ?? "Delete failed.");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Delete failed.");
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const user = employee?.user;
  const profileEmail = (user?.email ?? employee?.email ?? "").trim();
  const profilePhone = (user?.phone ?? employee?.phone ?? "").trim();
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    [employee?.firstName, employee?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    profileEmail ||
    "—";
  const avatarImage = (user?.avatar && `${user.avatar}`) || undefined;

  const deptDesigLine = employee
    ? [
        departmentDisplayName(employee),
        designationDisplayName(employee),
        employmentCategoryDisplayName(employee),
      ]
        .filter((s) => s && s !== "—")
        .join(" · ")
    : "";

  const tabQuery = searchParams.get("tab");
  const activeTab = employeeOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = useCallback(
    (tabId: string) => {
      const param = searchParamForEmployeeOverviewTabId(tabId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!param || param === EmployeeOverviewTabParam.OVERVIEW) {
            next.delete("tab");
          } else {
            next.set("tab", param);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (tabQuery && !isValidEmployeeOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  useEffect(() => {
    if (activeTab === "2" && !canReadSalaryStructures) {
      setTabInUrl("1");
    }
  }, [activeTab, canReadSalaryStructures, setTabInUrl]);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <Row className="align-items-center g-3">
                      <div className="col-auto">
                        <div className="avatar-lg rounded-circle overflow-hidden">
                          <Avatar
                            name={displayName}
                            image={avatarImage}
                            className="fs-20 w-100 h-100 img-thumbnail"
                            lightbox
                          />
                        </div>
                      </div>
                      <div className="col-md">
                        <div>
                          <h4 className="fw-bold">{displayName}</h4>
                          {employee?.code?.trim() ? (
                            <p className="text-muted mb-1 small">
                              <span className="text-uppercase text-muted me-1">
                                ID
                              </span>
                              <span className="fw-medium font-monospace text-body">
                                {employee.code}
                              </span>
                            </p>
                          ) : null}
                          {deptDesigLine ? (
                            <p className="text-muted mb-2">
                              <i className="ri-building-2-line me-1 align-middle" />
                              {deptDesigLine}
                            </p>
                          ) : null}
                          <div className="hstack gap-3 flex-wrap text-muted small">
                            <div className="d-inline-flex align-items-center gap-1">
                              <i className="ri-calendar-event-line" />
                              <span>
                                Joined{" "}
                                <span className="fw-medium text-body">
                                  {employee?.joinDate ? (
                                    <DateOnly date={employee.joinDate} />
                                  ) : (
                                    "—"
                                  )}
                                </span>
                              </span>
                            </div>
                            {profileEmail ? (
                              <>
                                <div className="vr d-none d-sm-block" />
                                <a
                                  href={`mailto:${profileEmail}`}
                                  className="d-inline-flex align-items-center gap-1 text-decoration-none text-muted"
                                >
                                  <i className="ri-mail-line" />
                                  <span className="text-break">
                                    {profileEmail}
                                  </span>
                                </a>
                              </>
                            ) : null}
                            {profilePhone ? (
                              <>
                                <div className="vr d-none d-sm-block" />
                                <a
                                  href={`tel:${profilePhone}`}
                                  className="d-inline-flex align-items-center gap-1 text-decoration-none text-muted"
                                >
                                  <i className="ri-phone-line" />
                                  {profilePhone}
                                </a>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </Row>
                  </div>
                  {(canEditEmployee || canDeleteUser) ? (
                    <div className="col-md-auto">
                      <div className="hstack gap-2">
                        {canEditEmployee ? (
                          <Link
                            to={`/employees/edit/${employee?.id}`}
                            className="btn btn-primary btn-sm"
                          >
                            <i className="ri-pencil-line align-bottom me-1" />
                            Edit
                          </Link>
                        ) : null}
                        {canDeleteUser ? (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => setDeleteModal(true)}
                          >
                            <i className="ri-delete-bin-line align-bottom me-1" />
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  {canReadSalaryStructures ? (
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "2" },
                          "text-body fw-semibold",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setTabInUrl("2");
                        }}
                        href="#"
                      >
                        Salary structures
                      </NavLink>
                    </NavItem>
                  ) : null}
                  {/* <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "3" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("3");
                      }}
                      href="#"
                    >
                      Bank details
                    </NavLink>
                  </NavItem> */}
                  {isOwnProfile ? (
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "4" },
                          "text-body fw-semibold",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setTabInUrl("4");
                        }}
                        href="#"
                      >
                        Payslips
                      </NavLink>
                    </NavItem>
                  ) : null}
                  {canReadEmployeeAdvances ? (
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "5" },
                          "text-body fw-semibold",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setTabInUrl("5");
                        }}
                        href="#"
                      >
                        Advances
                      </NavLink>
                    </NavItem>
                  ) : null}
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            {canReadSalaryStructures ? (
              <TabPane tabId="2">
                <SalaryStructuresTab />
              </TabPane>
            ) : null}
            <TabPane tabId="3">
              <BankAccountsTab />
            </TabPane>
            {isOwnProfile ? (
              <TabPane tabId="4">
                <EmployeePayslipsTab />
              </TabPane>
            ) : null}
            {canReadEmployeeAdvances ? (
              <TabPane tabId="5">
                <EmployeeAdvancesTab />
              </TabPane>
            ) : null}
          </TabContent>
        </Col>
      </Row>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        description="Deleting this user will also permanently remove their linked employee record, salary structures, bank accounts, and all associated data."
      />
    </React.Fragment>
  );
};

export default Section;
